/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

// frontend/src/services/api.js
//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
/**
 * SkillDelivery Platform - Frontend
 * 
 * Copyright (c) 2026 SkillDelivery.co.in
 * A product of SKD RATWANI TECHNOLOGY & SOFTWARE SOLUTIONS PRIVATE LIMITED
 * 
 * All rights reserved. This software and associated documentation files (the "Software")
 * are proprietary to SKD RATWANI TECHNOLOGY & SOFTWARE SOLUTIONS PRIVATE LIMITED.
 * 
 * Unauthorized copying, distribution, modification, or use of this Software,
 * via any medium, is strictly prohibited without express written permission.
 * 
 * For licensing inquiries: contact@skilldelivery.co.in
 */

// frontend/src/services/api.js - updated
// Ensure API_URL always ends with /api
const RAW_API_URL = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
const API_URL = RAW_API_URL.endsWith('/api') ? RAW_API_URL : `${RAW_API_URL.replace(/\/$/, '')}/api`;

class APIService {
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // ═══════════════════════════════════════════════════════════════
      // SESSION REVOKED HANDLING ("This wasn't me" / admin force-logout)
      // Any 401 with code:SESSION_REVOKED means the server has bumped
      // sessionsRevokedAt on this user; every token minted before that
      // is dead. Clear local state, drop a flash message, and hard
      // redirect to /login. DO NOT try to refresh — refreshToken() will
      // itself return SESSION_REVOKED, but we short-circuit here so the
      // very first request that surfaces the revoke is enough.
      // ═══════════════════════════════════════════════════════════════
      if (response.status === 401 && data && data.code === 'SESSION_REVOKED') {
        console.warn('[API] SESSION_REVOKED — clearing tokens and redirecting to /login');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tempToken');
        localStorage.removeItem('user');
        try {
          sessionStorage.setItem('flashMessage', 'You were signed out. Please sign in again.');
        } catch (_) { /* private mode / quota — flash is best-effort */ }
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        const error = new Error('SESSION_REVOKED');
        error.response = { data, status: 401 };
        error.code = 'SESSION_REVOKED';
        throw error;
      }

      // ═══════════════════════════════════════════════════════════════
      // ACCOUNT SUSPENSION / FORCED LOGOUT HANDLING
      // Intercepts ALL API responses for suspension (mid-session + login)
      // ═══════════════════════════════════════════════════════════════
      if (data.forceLogout === true || data.code === 'ACCOUNT_SUSPENDED' || data.error === 'ACCOUNT_SUSPENDED' || data.isSuspended === true || data.code === 'LOGIN_BLOCKED_SUSPENDED') {
        console.error('[API] ACCOUNT SUSPENDED - redirecting to /suspended');

        // Store suspension info for the Suspended page
        if (data.suspensionInfo) {
          localStorage.setItem('suspensionInfo', JSON.stringify(data.suspensionInfo));
        }

        // Clear all auth tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tempToken');
        localStorage.removeItem('user');

        // Redirect to suspension page immediately
        if (window.location.pathname !== '/suspended') {
          window.location.href = '/suspended';
        }

        const error = new Error(data.message || data.error || 'Account suspended');
        error.response = { data, status: response.status };
        error.isSuspended = true;
        throw error;
      }

      // ═══════════════════════════════════════════════════════════════
      // PASSWORD EXPIRY HANDLING
      // ═══════════════════════════════════════════════════════════════
      if (data.code === 'PASSWORD_EXPIRED') {
        // Store temporary token for password change
        if (data.temporaryToken) {
          localStorage.setItem('tempToken', data.temporaryToken);
        }
        // Redirect to password change page
        window.location.href = '/change-password?expired=true';
        throw new Error('Password expired - redirecting to change password');
      }

      // ═══════════════════════════════════════════════════════════════
      // ACCOUNT LOCKOUT HANDLING
      // ═══════════════════════════════════════════════════════════════
      if (response.status === 423 && data.code === 'ACCOUNT_LOCKED') {
        const error = new Error(data.error || 'Account is locked');
        error.response = { data, status: 423 };
        error.isLockout = true;
        error.remainingMinutes = data.remainingMinutes;
        throw error;
      }

      if (!response.ok) {
        const error = new Error(data.error || 'API request failed');
        error.response = { data, status: response.status };
        error.attemptsRemaining = data.attemptsRemaining;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Generic HTTP methods
  // ═══════════════════════════════════════════════════════════════════════════

  async get(endpoint) {
    return await this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data) {
    return await this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return await this.request(endpoint, { method: 'DELETE' });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async login(email, password, captchaToken) {
    // captchaToken is optional. Backend requires it (web) only after 2
    // failed logins on this email or IP in the last 15 min — server tells
    // us so via `captcha_required: true` on a 401 response; the login form
    // then mounts the widget and resubmits with the token.
    const body = { email, password };
    if (captchaToken) body['cf-turnstile-response'] = captchaToken;
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    // V2.0: Check if 2FA is required (don't store token yet)
    if (data.success && data.requires2FA) {
      return data; // Return as-is, no token storage
    }

    if (data.success && data.data?.token) {
      localStorage.setItem('token', data.data.token);
      if (data.data.refreshToken) {
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }
    }

    return data;
  }

  // V2.0: Verify 2FA code and complete login
  async verify2FA(tempToken, token) {
    const data = await this.request('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ tempToken, token }),
    });

    if (data.success && data.data?.token) {
      localStorage.setItem('token', data.data.token);
      if (data.data.refreshToken) {
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }
    }

    return data;
  }

  async register(userData) {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    // Don't auto-login after registration - user needs to verify email first
    // Token will only be stored after successful login post-verification

    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tempToken');
      localStorage.removeItem('user');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASSWORD POLICY ENDPOINTS (NEW)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get password policy rules
   */
  async getPasswordPolicy() {
    return await this.request('/auth/password-policy', { method: 'GET' });
  }

  /**
   * Validate password against policy (client-side helper)
   */
  async validatePassword(password, context = {}) {
    return await this.request('/auth/validate-password', {
      method: 'POST',
      body: JSON.stringify({ password, ...context }),
    });
  }

  /**
   * Change password (requires current password)
   */
  async changePassword(currentPassword, newPassword) {
    // Use temp token if available (for expired password flow)
    const tempToken = localStorage.getItem('tempToken');
    const headers = {};
    
    if (tempToken) {
      headers['Authorization'] = `Bearer ${tempToken}`;
    }

    const data = await this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
      headers,
    });

    // Clear temp token after successful change
    if (data.success) {
      localStorage.removeItem('tempToken');
      // Update main token if new one provided
      if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
      }
      if (data.data?.refreshToken) {
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }
    }

    return data;
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email, captchaToken) {
    const body = { email };
    if (captchaToken) body['cf-turnstile-response'] = captchaToken;
    return await this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token, newPassword) {
    return await this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token) {
    return await this.request(`/auth/verify-email?token=${token}`, {
      method: 'GET',
    });
  }

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    return await this.request('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const data = await this.request('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (data.success && data.data?.token) {
      localStorage.setItem('token', data.data.token);
      if (data.data.refreshToken) {
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }
    }

    return data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFILE ENDPOINTS (Professional Profile Upload/Management)
  // ═══════════════════════════════════════════════════════════════════════════

  async uploadResume(formData, userId) {
    const token = localStorage.getItem('token');

    // Append userId to formData
    formData.append('userId', userId);

    const response = await fetch(`${API_URL}/resume/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    console.log('Upload response:', data);
    return data;
  }

  /**
   * Upload a platform-format network CSV (row_id,company,position).
   *
   * Mirrors uploadResume: normalised API_URL as single source of URL
   * truth, Bearer token from localStorage, multipart FormData with NO
   * Content-Type header so the browser attaches the multipart boundary.
   * Returns the parsed response body as-is; caller checks .success.
   *
   * Signature is intentionally (file) only — no userId. The server
   * derives userId from the JWT (S1/S2 IDOR fix, 2026-08-27) and
   * ignores any userId sent in the body.
   */
  async uploadNetworkCsv(file) {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/network/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    return data;
  }

  async getResume(userId) {
    return await this.request(`/resume/${userId}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFILE HISTORY ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get profile history for a user
   */
  async getProfileHistory(userId) {
    return await this.request(`/resume/history/${userId}`);
  }

  /**
   * Activate a specific profile (set as current)
   */
  async activateProfile(resumeId) {
    return await this.request(`/resume/activate/${resumeId}`, {
      method: 'POST'
    });
  }

  /**
   * Delete a profile from history
   */
  async deleteProfile(resumeId) {
    return await this.request(`/resume/profile/${resumeId}`, {
      method: 'DELETE'
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ROADMAP ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async generateRoadmap(userId, vertical, llmPreference = 'auto', modelName = 'llama3.1:8b') {
    // Use this.request() so Authorization: Bearer <token> is attached
    // by the standard header injection (api.js:56-59). Raw fetch() here
    // sent no auth header, generating [authz:*] NO_TOKEN log lines and
    // — once GLOBAL_AUTH_MODE flips to enforce — would 401 the whole
    // roadmap-generation flow.
    return await this.request('/roadmap/generate', {
      method: 'POST',
      body: JSON.stringify({ userId, vertical, llmPreference, modelName }),
    });
  }

  async getRoadmap(userId, vertical) {
    const query = vertical ? `?vertical=${vertical}` : '';
    return await this.request(`/roadmap/${userId}${query}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KNOWLEDGE GRAPH ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async getKPIForOperation(userId) {
    return await this.request(`/kg/kpi/${userId}`);
  }

  async getSkillRelationships(userId) {
    return await this.request(`/kg/skills/relationships/${userId}`);
  }

  async getLearningPathOptimization(userId) {
    return await this.request(`/kg/learning/optimize/${userId}`);
  }

  async getCareerTrajectory(userId) {
    return await this.request(`/kg/career/trajectory/${userId}`);
  }

  async getSkillClusters(userId) {
    return await this.request(`/kg/skills/clusters/${userId}`);
  }

  async findSimilarProfiles(userId) {
    return await this.request(`/kg/mentors/similar/${userId}`);
  }

  async getProfileGraph(userId) {
    return await this.request(`/kg/profile/${userId}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // USER ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async getUser(userId) {
    return await this.request(`/users/${userId}`);
  }

  async updateUser(userId, userData) {
    return await this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAT ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async sendChatMessage(userId, message) {
    return await this.request('/chat/message', {
      method: 'POST',
      body: JSON.stringify({ userId, message })
    });
  }

  async getChatHistory(userId, options = {}) {
    const { limit = 50, vertical } = options;
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (vertical) params.append('vertical', vertical);
    
    return await this.request(`/conversation/history/${userId}?${params.toString()}`);
  }

  async getConversationContext(userId) {
    return await this.request(`/conversation/context/${userId}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LEARNING RESOURCES ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async getRecommendations(userId, limit = 10) {
    return await this.request(`/resources/recommendations/${userId}?limit=${limit}`);
  }

  async searchResources(filters = {}) {
    const params = new URLSearchParams();
    
    if (filters.vertical) params.append('vertical', filters.vertical);
    if (filters.level) params.append('level', filters.level);
    if (filters.type) params.append('type', filters.type);
    if (filters.isFree) params.append('isFree', 'true');
    if (filters.certificate) params.append('certificate', 'true');
    if (filters.search) params.append('q', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    
    return await this.request(`/resources/search?${params.toString()}`);
  }

  async getResourcesByLevel(vertical, level, limit = 5) {
    return await this.request(`/resources/by-level/${vertical}/${level}?limit=${limit}`);
  }

  async saveResource(userId, resourceId) {
    return await this.request('/resources/save', {
      method: 'POST',
      body: JSON.stringify({ userId, resourceId })
    });
  }

  async updateResourceProgress(userId, resourceId, progressData) {
    return await this.request('/resources/progress', {
      method: 'PUT',
      body: JSON.stringify({ userId, resourceId, ...progressData })
    });
  }

  async getMyLearning(userId, status = null) {
    const url = status 
      ? `/resources/my-learning/${userId}?status=${status}`
      : `/resources/my-learning/${userId}`;
    return await this.request(url);
  }

  async getLearningStats(userId) {
    return await this.request(`/resources/stats/${userId}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DOCUMENT MANAGEMENT ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async uploadDocument(formData) {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_URL}/documents/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    return await response.json();
  }

  async getDocuments(filters = {}) {
    const params = new URLSearchParams();
    
    if (filters.vertical) params.append('vertical', filters.vertical);
    if (filters.category) params.append('category', filters.category);
    if (filters.isProcessed !== undefined) params.append('isProcessed', filters.isProcessed);
    if (filters.limit) params.append('limit', filters.limit);
    
    return await this.request(`/documents?${params.toString()}`);
  }

  async getDocument(documentId) {
    return await this.request(`/documents/${documentId}`);
  }

  async searchDocuments(query, vertical = null) {
    const url = vertical 
      ? `/documents/search/${encodeURIComponent(query)}?vertical=${vertical}`
      : `/documents/search/${encodeURIComponent(query)}`;
    return await this.request(url);
  }

  async deleteDocument(documentId) {
    return await this.request(`/documents/${documentId}`, {
      method: 'DELETE'
    });
  }

  async reprocessDocument(documentId) {
    return await this.request(`/documents/${documentId}/reprocess`, {
      method: 'POST'
    });
  }

  async getDocumentStats(vertical = null) {
    const url = vertical ? `/documents/stats/${vertical}` : '/documents/stats';
    return await this.request(url);
  }
}

const apiService = new APIService();
export default apiService;
